import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import socketService from '../services/socket';
import webrtcService from '../services/webrtc';
import { recordingsAPI } from '../services/api';

const RoomContext = createContext(null);

export const useRoom = () => {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error('useRoom must be used within a RoomProvider');
  }
  return context;
};

export const RoomProvider = ({ children }) => {
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [messages, setMessages] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [userName, setUserName] = useState('');
  const [error, setError] = useState(null);
  const [screenShareRequest, setScreenShareRequest] = useState(null); // null, 'pending', 'approved', 'denied'
  const [screenShareRequests, setScreenShareRequests] = useState([]); // Array of { participantId, participantName }
  const [waitingRoomUsers, setWaitingRoomUsers] = useState([]); // Array of waiting users
  const [screenSharingUserId, setScreenSharingUserId] = useState(null); // ID de l'utilisateur qui partage son écran
  const [handRaised, setHandRaised] = useState(false);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  
  // Recording refs for MediaRecorder
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const canvasRef = useRef(null);
  const canvasStreamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isRecordingRef = useRef(false); // Use ref to avoid stale closures
  const remoteStreamsRef = useRef(new Map()); // Ref for remote streams
  
  // Cache for video elements (must be declared before cleanup)
  const videoCacheRef = useRef(new Map());
  
  // Utilisez des références pour éviter les problèmes de dépendance dans useEffect
  const localStreamRef = useRef(null);
  const startScreenShareRef = useRef(null);

  // Fonction de nettoyage
  const cleanup = useCallback(() => {
    // Stop recording if active
    if (isRecordingRef.current && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
      canvasRef.current = null;
      if (canvasStreamRef.current) {
        canvasStreamRef.current.getTracks().forEach(track => track.stop());
        canvasStreamRef.current = null;
      }
      // Clear video cache
      videoCacheRef.current.forEach(video => {
        video.srcObject = null;
      });
      videoCacheRef.current.clear();
      setIsRecording(false);
      isRecordingRef.current = false;
      setRecordingStartTime(null);
    }
    
    webrtcService.cleanup();
    // Do NOT disconnect the socket here, otherwise Home.js stays with isConnected = false
    // and the user cannot start/join a new meeting without a page refresh.
    setRoom(null);
    setParticipants([]);
    setLocalStream(null);
    localStreamRef.current = null;
    setRemoteStreams(new Map());
    setMessages([]);
    setIsMuted(false);
    setIsVideoOff(false);
    setIsScreenSharing(false);
    setError(null);
    setScreenShareRequest(null);
    setScreenShareRequests([]);
    setWaitingRoomUsers([]);
    setScreenSharingUserId(null);
    setHandRaised(false);
    setUserName('');
  }, []);

  // Gestionnaires d'événements définis avant useEffect
  const handleUserJoined = useCallback(async ({ participant, room: updatedRoom }) => {
    console.log('[RoomContext] User joined event:', participant);
    setRoom(updatedRoom);
    setParticipants(updatedRoom.participants);
    
    if (participant && participant.id) {
      if (!localStreamRef.current) {
        try {
          const stream = await webrtcService.getLocalStream();
          if (stream) {
            setLocalStream(stream);
            localStreamRef.current = stream;
          } else {
            console.warn('[RoomContext] No local media stream, continuing without tracks');
          }
        } catch (err) {
          console.error('Failed to initialize media:', err);
        }
      }
      console.log('[RoomContext] Existing peer will answer offers from new participant:', participant.id);
    }
  }, []);

  const handleUserLeft = useCallback(({ odId, room: updatedRoom }) => {
    console.log('User left:', odId);
    if (updatedRoom) {
      setRoom(updatedRoom);
      setParticipants(updatedRoom.participants);
    }
    setRemoteStreams((prev) => {
      const newMap = new Map(prev);
      newMap.delete(odId);
      return newMap;
    });
    webrtcService.closePeerConnection(odId);
  }, []);


  //gout gout gout
  const handleExistingPeers = useCallback(async ({ peers }) => {
    console.log('[RoomContext] Existing peers received:', peers.map((peer) => peer.id).join(', ') || 'none');

    if (!localStreamRef.current) {
      const stream = await webrtcService.getLocalStream();
      if (stream) {
        setLocalStream(stream);
        localStreamRef.current = stream;
      }
    }

    for (const peer of peers) {
      console.log('[RoomContext] New participant creating offer to existing peer:', peer.id);
      await webrtcService.createOffer(peer.id);
    }
  }, []);

  const handleNewPeer = useCallback(async ({ peerId }) => {
    console.log('[RoomContext] New peer event ignored for offer creation:', peerId);

    if (!localStreamRef.current) {
      const stream = await webrtcService.getLocalStream();
      if (stream) {
        setLocalStream(stream);
        localStreamRef.current = stream;
      }
    }
  }, []);
  //commentaire moko bien

  const handleOffer = useCallback(async ({ senderId, offer }) => {
    console.log('Received offer from:', senderId);
    await webrtcService.handleOffer(senderId, offer);
  }, []);

  const handleAnswer = useCallback(async ({ senderId, answer }) => {
    console.log('Received answer from:', senderId);
    await webrtcService.handleAnswer(senderId, answer);
  }, []);

  const handleIceCandidate = useCallback(async ({ senderId, candidate }) => {
    await webrtcService.handleIceCandidate(senderId, candidate);
  }, []);

  const handleParticipantAudioToggle = useCallback(({ odId, isMuted: muted }) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === odId ? { ...p, isMuted: muted } : p))
    );
  }, []);

  const handleParticipantVideoToggle = useCallback(({ odId, isVideoOff: videoOff }) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === odId ? { ...p, isVideoOff: videoOff } : p))
    );
  }, []);

  const handleScreenShareStarted = useCallback(({ odId, userName: name }) => {
    console.log(`${name} started screen sharing`);
    setScreenSharingUserId(odId);
  }, []);

  const handleScreenShareStopped = useCallback(({ odId }) => {
    console.log('Screen sharing stopped by:', odId);
    if (screenSharingUserId === odId) {
      setScreenSharingUserId(null);
    }
  }, [screenSharingUserId]);

  const handleScreenShareRequest = useCallback(({ participantId, participantName }) => {
    console.log(`Screen share request from ${participantName} (${participantId})`);
    // Show notification to host
    setScreenShareRequests(prev => [...prev, { participantId, participantName }]);
  }, []);

  const handleScreenShareApproved = useCallback(() => {
    console.log('Screen share request approved');
    setScreenShareRequest('approved');
    if (startScreenShareRef.current) {
      startScreenShareRef.current();
    }
  }, []);

  const handleScreenShareDenied = useCallback(() => {
    console.log('Screen share request denied');
    setScreenShareRequest('denied');
  }, []);

  const handleForceMute = useCallback(() => {
    console.log('Force muted by host');
    setIsMuted(true);
    webrtcService.toggleAudio(true);
  }, []);

  const handleKickedFromMeeting = useCallback(() => {
    console.log('Kicked from meeting');
    alert('Vous avez été exclu de la réunion par l\'hôte.');
    cleanup();
  }, [cleanup]);

  const handleMeetingEnded = useCallback(() => {
    console.log('Meeting ended by host');
    alert('La réunion a été terminée par l\'hôte.');
    cleanup();
  }, [cleanup]);

  const handleChatMessage = useCallback((data) => {
    if (!room) return;
    const codes = [room.name, room.id]; // room.name = meeting_code depuis PersistentRoom/RoomManager
    if (data.id_meeting !== undefined && !codes.some((c) => String(c) === String(data.id_meeting))) return;
    if (data.roomId     !== undefined && !codes.some((c) => String(c) === String(data.roomId)))     return;

    const msg = {
      id:        data.id           || data.idmessage,
      message:   data.message      || data.contenu,
      content:   data.message      || data.contenu,
      senderId:  data.senderId     || data.iduser,
      senderName: data.senderName  || (data.userPrenom && data.userName
                              ? `${data.userPrenom} ${data.userName}`.trim()
                              : (data.userName  || 'Unknown')),
      timestamp: data.timestamp    || data.dateEnvoie,
      type:      data.type         || 'text',
    };

    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, [room]);

  const handleWaitingRoomUpdated = useCallback(({ waitingUsers }) => {
    console.log('Waiting room updated:', waitingUsers);
    setWaitingRoomUsers(waitingUsers);
  }, []);

  const handleRoomUsers = useCallback(({ users }) => {
    console.log('Room users updated:', users);
    setParticipants(users);
  }, []);

const handleParticipantHandRaised = useCallback(({ odId, handRaised }) => {
     console.log('Participant hand raised:', odId, handRaised);
     setParticipants((prev) =>
       prev.map((p) =>
         p.id === odId ? { ...p, handRaised } : p
       )
     );
   }, []);

   const handleChatHistory = useCallback((data) => {
     console.log('Chat history received:', data.messages?.length || 0, 'messages');
     if (data.messages && Array.isArray(data.messages)) {
       setMessages(data.messages);
     }
   }, []);

  // Initialize socket connection
  useEffect(() => {
    const socket = socketService.connect();
    
    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    return () => {
      // Ne pas déconnecter le socket lors du démontage du composant
      // Le socket reste connecté pour les autres composants
    };
  }, []);

  // Setup socket event listeners
  useEffect(() => {
    if (!isConnected) return;

    // Room events
    socketService.on('user-joined', handleUserJoined);
    socketService.on('user-left', handleUserLeft);

    // Signaling events
    socketService.on('existing-peers', handleExistingPeers);
    socketService.on('new-peer', handleNewPeer);
    socketService.on('offer', handleOffer);
    socketService.on('answer', handleAnswer);
    socketService.on('ice-candidate', handleIceCandidate);

    // Media events
    socketService.on('participant-audio-toggle', handleParticipantAudioToggle);
    socketService.on('participant-video-toggle', handleParticipantVideoToggle);
    socketService.on('screen-share-started', handleScreenShareStarted);
    socketService.on('screen-share-stopped', handleScreenShareStopped);
    socketService.on('screen-share-request', handleScreenShareRequest);
    socketService.on('screen-share-approved', handleScreenShareApproved);
    socketService.on('screen-share-denied', handleScreenShareDenied);
    socketService.on('force-mute', handleForceMute);
    socketService.on('kicked-from-meeting', handleKickedFromMeeting);
    socketService.on('meeting-ended', handleMeetingEnded);

// Chat events
     socketService.on('chat-message', handleChatMessage);
     socketService.on('chat-history', handleChatHistory);

    // Waiting room events
    socketService.on('waiting-room-updated', handleWaitingRoomUpdated);
    socketService.on('room-users', handleRoomUsers);

    // Hand events
    socketService.on('participant-hand-raised', handleParticipantHandRaised);

    return () => {
      socketService.off('user-joined', handleUserJoined);
      socketService.off('user-left', handleUserLeft);
      socketService.off('existing-peers', handleExistingPeers);
      socketService.off('new-peer', handleNewPeer);
      socketService.off('offer', handleOffer);
      socketService.off('answer', handleAnswer);
      socketService.off('ice-candidate', handleIceCandidate);
      socketService.off('participant-audio-toggle', handleParticipantAudioToggle);
      socketService.off('participant-video-toggle', handleParticipantVideoToggle);
      socketService.off('screen-share-started', handleScreenShareStarted);
      socketService.off('screen-share-stopped', handleScreenShareStopped);
      socketService.off('screen-share-request', handleScreenShareRequest);
      socketService.off('screen-share-approved', handleScreenShareApproved);
      socketService.off('screen-share-denied', handleScreenShareDenied);
      socketService.off('force-mute', handleForceMute);
      socketService.off('kicked-from-meeting', handleKickedFromMeeting);
      socketService.off('meeting-ended', handleMeetingEnded);
socketService.off('chat-message', handleChatMessage);
       socketService.off('chat-history', handleChatHistory);
       socketService.off('waiting-room-updated', handleWaitingRoomUpdated);
      socketService.off('room-users', handleRoomUsers);
      socketService.off('participant-hand-raised', handleParticipantHandRaised);
    };
  }, [
    isConnected,
    handleUserJoined,
    handleUserLeft,
    handleExistingPeers,
    handleNewPeer,
    handleOffer,
    handleAnswer,
    handleIceCandidate,
    handleParticipantAudioToggle,
    handleParticipantVideoToggle,
    handleScreenShareStarted,
    handleScreenShareStopped,
    handleScreenShareRequest,
    handleScreenShareApproved,
    handleScreenShareDenied,
    handleForceMute,
    handleKickedFromMeeting,
    handleMeetingEnded,
handleChatMessage,
     handleChatHistory,
     handleWaitingRoomUpdated,
    handleRoomUsers,
    handleParticipantHandRaised,
  ]);

  // Setup WebRTC callbacks
  useEffect(() => {
    webrtcService.onRemoteStream = (peerId, stream) => {
      console.log('Remote stream received from:', peerId, 'tracks:', stream.getTracks().length, stream.getTracks().map((track) => `${track.kind}:${track.readyState}`).join(','));
      setRemoteStreams((prev) => {
        const newMap = new Map(prev);
        newMap.set(peerId, stream);
        remoteStreamsRef.current = newMap; // Keep ref in sync
        return newMap;
      });
    };

    webrtcService.onPeerDisconnected = (peerId) => {
      setRemoteStreams((prev) => {
        const newMap = new Map(prev);
        newMap.delete(peerId);
        remoteStreamsRef.current = newMap; // Keep ref in sync
        return newMap;
      });
    };
  }, []);

  // Actions
const initializeMedia = useCallback(async () => {
     try {
       const stream = await webrtcService.getLocalStream();
       if (stream) {
         setLocalStream(stream);
         localStreamRef.current = stream;
       } else {
         console.warn('No media stream available, continuing without video/audio');
         localStreamRef.current = null;
       }
       return stream;
     } catch (err) {
       console.error('Error initializing media:', err);
       setLocalStream(null);
       localStreamRef.current = null;
       // Don't throw - allow continuing without media
       return null;
     }
   }, []);

  const createRoom = useCallback(async (roomName, name) => {
    try {
      setUserName(name);
      await initializeMedia();
      const response = await socketService.createRoom(roomName, name);
      setRoom(response.room);
      setParticipants(response.room.participants);
      socketService.requestPeerConnections();
      return response.room;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [initializeMedia]);

const joinRoom = useCallback(async (roomId, name) => {
     try {
       setUserName(name);
       await initializeMedia();
       const response = await socketService.joinRoom(roomId, name);
       setRoom(response.room);
       setParticipants(response.room.participants);
       // Note: existing-peers and chat-history are sent by the server, no need to call requestPeerConnections
       return response.room;
     } catch (err) {
       setError(err.message);
       throw err;
     }
   }, [initializeMedia]);

const startRoomCall = useCallback(async (roomId, name) => {
     try {
       setUserName(name);
       await initializeMedia();
       const response = await socketService.startRoomCall(roomId, name);
       setRoom(response.room);
       setParticipants(response.room.participants);
       // Note: existing-peers and chat-history are sent by the server, no need to call requestPeerConnections
       return response.room;
     } catch (err) {
       setError(err.message);
       throw err;
     }
   }, [initializeMedia]);

  const handleWaitingRoomApproved = useCallback(async ({ room: approvedRoom }) => {
    console.log('Approved from waiting room:', approvedRoom);
    setRoom(approvedRoom);
    setParticipants(approvedRoom.participants);
    await initializeMedia();
  }, [initializeMedia]);

  const leaveRoom = useCallback(async () => {
    if (room) {
      try {
        await socketService.leaveRoom(room.id);
      } catch (err) {
        // Ignore "Room not found" errors as they can occur when room was already deleted
        if (err.message && err.message.includes('Room not found')) {
          console.warn('Room already left or not found:', err.message);
        } else {
          console.error('Error leaving room:', err);
        }
      }
    }
    cleanup();
  }, [room, cleanup]);

  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    webrtcService.toggleAudio(newMuted);
    socketService.toggleAudio(newMuted);
  }, [isMuted]);

  const toggleVideo = useCallback(() => {
    const newVideoOff = !isVideoOff;
    setIsVideoOff(newVideoOff);
    webrtcService.toggleVideo(newVideoOff);
    socketService.toggleVideo(newVideoOff);
  }, [isVideoOff]);

  const toggleHand = useCallback(() => {
    const newHandRaised = !handRaised;
    setHandRaised(newHandRaised);
    if (newHandRaised) {
      socketService.raiseHand();
    } else {
      socketService.lowerHand();
    }
  }, [handRaised]);

  const stopScreenShare = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      webrtcService.replaceVideoTrack(videoTrack);
    }
    webrtcService.stopScreenStream();
    socketService.stopScreenShare();
    setIsScreenSharing(false);
  }, []);

  const startScreenShare = useCallback(async () => {
    try {
      const screenStream = await webrtcService.getScreenStream();
      const videoTrack = screenStream.getVideoTracks()[0];
      
      webrtcService.replaceVideoTrack(videoTrack);
      socketService.startScreenShare();
      setIsScreenSharing(true);

      videoTrack.onended = () => {
        stopScreenShare();
      };
    } catch (err) {
      console.error('Error starting screen share:', err);
    }
  }, [stopScreenShare]);

  // Set ref for startScreenShare
  useEffect(() => {
    startScreenShareRef.current = startScreenShare;
  }, [startScreenShare]);

  const sendMessage = useCallback(async (message) => {
    try {
      await socketService.sendMessage(message);
    } catch (err) {
      console.error('Error sending message:', err);
    }
  }, []);

  // Request screen share permission (for participants)
  const requestScreenShare = useCallback(() => {
    setScreenShareRequest('pending');
    socketService.requestScreenShare();
  }, []);

  // Approve screen share request (for host)
  const approveScreenShare = useCallback((participantId) => {
    socketService.approveScreenShare(participantId);
    setScreenShareRequests(prev => prev.filter(req => req.participantId !== participantId));
  }, []);

  // Deny screen share request (for host)
  const denyScreenShare = useCallback((participantId) => {
    socketService.denyScreenShare(participantId);
    setScreenShareRequests(prev => prev.filter(req => req.participantId !== participantId));
  }, []);

  // Approve user from waiting room (host only)
  const approveFromWaitingRoom = useCallback((participantId) => {
    socketService.approveFromWaitingRoom(participantId);
    setWaitingRoomUsers(prev => prev.filter(user => user.id !== participantId));
  }, []);

  // Deny user from waiting room (host only)
  const denyFromWaitingRoom = useCallback((participantId) => {
    socketService.denyFromWaitingRoom(participantId);
    setWaitingRoomUsers(prev => prev.filter(user => user.id !== participantId));
  }, []);

  // Mute a specific participant (host only)
  const muteParticipant = useCallback((participantId) => {
    socketService.muteParticipant(participantId);
  }, []);

  // Mute all participants (host only)
  const muteAllParticipants = useCallback(() => {
    socketService.muteAllParticipants();
  }, []);

  // Kick a participant (host only)
  const kickParticipant = useCallback((participantId) => {
    socketService.kickParticipant(participantId);
  }, []);

  // End meeting for everyone (host only)
  const endMeeting = useCallback(() => {
    socketService.endMeeting();
    cleanup();
  }, [cleanup]);

  // Helper function to draw all streams to canvas
  const drawStreamsToCanvas = useCallback(() => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      const width = canvas.width;
      const height = canvas.height;
      
      // Clear canvas
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, width, height);
      
      // Get all streams to record (local + remote)
      const allStreams = [];
      if (localStreamRef.current) {
        allStreams.push({ stream: localStreamRef.current, peerId: 'local' });
      }
      
      // Safely iterate over remote streams using ref
      if (remoteStreamsRef.current && remoteStreamsRef.current instanceof Map) {
        remoteStreamsRef.current.forEach((stream, peerId) => {
          allStreams.push({ stream, peerId });
        });
      }
      
      // Calculate grid layout
      const count = allStreams.length;
      if (count === 0) {
        // Draw "Enregistrement en cours" text
        ctx.fillStyle = '#ffffff';
        ctx.font = '24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Enregistrement en cours...', width / 2, height / 2);
      } else if (count === 1) {
        // Single participant - full screen
        const streamData = allStreams[0];
        let video = videoCacheRef.current.get(streamData.peerId);
        
        if (!video) {
          video = document.createElement('video');
          video.muted = true;
          video.autoplay = true;
          video.playsInline = true;
          videoCacheRef.current.set(streamData.peerId, video);
        }
        
        if (video.srcObject !== streamData.stream) {
          video.srcObject = streamData.stream;
          video.play().catch(() => {});
        }
        
        // Check if video has loaded frames
        if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
          ctx.drawImage(video, 0, 0, width, height);
        } else {
          // Draw placeholder
          ctx.fillStyle = '#333333';
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = '#ffffff';
          ctx.font = '24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Participant 1', width / 2, height / 2);
        }
      } else {
        // Multiple participants - grid layout
        const cols = Math.ceil(Math.sqrt(count));
        const rows = Math.ceil(count / cols);
        const cellWidth = width / cols;
        const cellHeight = height / rows;
        
        allStreams.forEach((item, index) => {
          const col = index % cols;
          const row = Math.floor(index / cols);
          const x = col * cellWidth;
          const y = row * cellHeight;
          
          let video = videoCacheRef.current.get(item.peerId);
          
          if (!video) {
            video = document.createElement('video');
            video.muted = true;
            video.autoplay = true;
            video.playsInline = true;
            videoCacheRef.current.set(item.peerId, video);
          }
          
          if (video.srcObject !== item.stream) {
            video.srcObject = item.stream;
            video.play().catch(() => {});
          }
          
          // Calculate aspect ratio fit
          const videoAspect = video.videoWidth > 0 ? video.videoWidth / video.videoHeight : 4/3;
          const cellAspect = cellWidth / cellHeight;
          let drawWidth = cellWidth;
          let drawHeight = cellHeight;
          let drawX = x;
          let drawY = y;
          
          if (videoAspect > cellAspect) {
            drawHeight = cellWidth / videoAspect;
            drawY = y + (cellHeight - drawHeight) / 2;
          } else {
            drawWidth = cellHeight * videoAspect;
            drawX = x + (cellWidth - drawWidth) / 2;
          }
          
          // Check if video has loaded frames
          if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
            ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
          } else {
            ctx.fillStyle = '#333333';
            ctx.fillRect(x, y, cellWidth, cellHeight);
            ctx.fillStyle = '#ffffff';
            ctx.font = '18px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(`Participant ${index + 1}`, x + cellWidth/2, y + cellHeight/2);
          }
        });
      }
      
      // Continue animation loop only if still recording
      if (isRecordingRef.current && canvasRef.current) {
        animationFrameRef.current = requestAnimationFrame(drawStreamsToCanvas);
      }
    } catch (err) {
      console.error('Error in drawStreamsToCanvas:', err);
    }
  }, []);

  // Recording functions - with MediaRecorder
  const startRecording = useCallback(async (userId, meetingId = null) => {
    try {
      // Create canvas for compositing all streams
      const canvas = document.createElement('canvas');
      canvas.width = 1280;  // HD resolution
      canvas.height = 720;
      canvasRef.current = canvas;
      
      // Get canvas stream (this will be recorded)
      const canvasStream = canvas.captureStream(30); // 30 fps
      canvasStreamRef.current = canvasStream;
      
      // Add local audio track if available (simplest approach)
      if (localStreamRef.current) {
        const audioTracks = localStreamRef.current.getAudioTracks();
        audioTracks.forEach(track => {
          canvasStream.addTrack(track);
        });
      }
      
      // Create MediaRecorder with canvas stream
      // Try different mime types for browser compatibility
      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8,opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }
      
      const mediaRecorder = new MediaRecorder(canvasStream, {
        mimeType: mimeType
      });

      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start(1000); // Collect data every second
      mediaRecorderRef.current = mediaRecorder;

      setIsRecording(true);
      isRecordingRef.current = true;
      setRecordingStartTime(new Date());
      
      // Start drawing streams to canvas
      drawStreamsToCanvas();

      // Notify other participants that recording started
      socketService.startRecording(meetingId);
    } catch (err) {
      console.error('Error starting recording:', err);
      // Clean up on error
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      canvasRef.current = null;
      setIsRecording(false);
      setRecordingStartTime(null);
    }
  }, [drawStreamsToCanvas]);

  const stopRecording = useCallback(async (userId, meetingId = null) => {
    try {
      // Stop animation loop first
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      // Wait a bit for last data
      await new Promise(resolve => setTimeout(resolve, 500));

      if (recordingStartTime && recordedChunksRef.current.length > 0) {
        const duration = new Date() - recordingStartTime;
        const hours = Math.floor(duration / 3600000);
        const minutes = Math.floor((duration % 3600000) / 60000);
        const seconds = Math.floor((duration % 60000) / 1000);
        const durationFormatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

        // Create blob from recorded chunks
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        
        // Create form data for upload
        const formData = new FormData();
        const fileName = `recording_${Date.now()}.webm`;
        formData.append('video', blob, fileName);
        formData.append('duration', durationFormatted);
        formData.append('userId', userId);
        if (meetingId) {
          formData.append('meetingId', meetingId);
        }

        // Upload to server
        await recordingsAPI.uploadRecording(formData);
      }

      // Cleanup
      setIsRecording(false);
      isRecordingRef.current = false;
      setRecordingStartTime(null);
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
      canvasRef.current = null;
      
      // Clear video cache
      videoCacheRef.current.forEach(video => {
        video.srcObject = null;
      });
      videoCacheRef.current.clear();
      
      if (canvasStreamRef.current) {
        canvasStreamRef.current.getTracks().forEach(track => track.stop());
        canvasStreamRef.current = null;
      }

      // Notify other participants that recording stopped
      socketService.stopRecording(meetingId);
    } catch (err) {
      console.error('Error stopping recording:', err);
      setIsRecording(false);
      setRecordingStartTime(null);
      canvasRef.current = null;
    }
  }, [recordingStartTime]);

const value = {
      room,
      setRoom,
      participants,
      setParticipants,
      localStream,
      remoteStreams,
      messages,
      isMuted,
      isVideoOff,
      isScreenSharing,
      isConnected,
      userName,
      error,
      socketId: socketService.getSocketId(),
      createRoom,
      joinRoom,
      startRoomCall,
      leaveRoom,
      toggleMute,
      toggleVideo,
      startScreenShare,
      stopScreenShare,
      sendMessage,
      setError,
      isRecording,
      recordingStartTime,
      startRecording,
      stopRecording,
      screenShareRequest,
      requestScreenShare,
      screenShareRequests,
      screenSharingUserId,
      approveScreenShare,
      denyScreenShare,
      muteParticipant,
      muteAllParticipants,
      kickParticipant,
      endMeeting,
      waitingRoomUsers,
      approveFromWaitingRoom,
      denyFromWaitingRoom,
      handRaised,
      toggleHand,
      initializeMedia,
    };

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
};

export default RoomContext;
